<template>
  <router-link
    v-if="to && !isDisabled && !isLoading"
    v-slot="{ navigate, href }"
    :to="to"
    custom
  >
    <button
      :class="buttonClasses"
      :disabled="isDisabled || isLoading"
      :href="href"
      v-bind="$attrs"
      @click="(e) => { handleClick(e); navigate(); }"
    >
      <span
        v-if="$slots.icon || icon"
        class="icon"
        :class="{ 'mr-2': $slots.default }"
      >
        <i
          v-if="icon"
          :class="icon"
        />
        <slot
          v-else
          name="icon"
        />
      </span>
      <slot />
    </button>
  </router-link>
  <button
    v-else
    :class="buttonClasses"
    :disabled="isDisabled || isLoading"
    v-bind="$attrs"
    @click="handleClick"
  >
    <span
      v-if="$slots.icon || icon"
      class="icon"
      :class="{ 'mr-2': $slots.default }"
    >
      <i
        v-if="icon"
        :class="icon"
      />
      <slot
        v-else
        name="icon"
      />
    </span>
    <slot />
  </button>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps({
  to: {
    type: [String, Object],
    default: null,
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => [
      'primary', 'success', 'info', 'warning', 'danger',
      'link', 'light', 'dark', 'text', 'indiefy-pink', 'indiefy-teal', 'spotify',
    ].includes(value),
  },
  size: {
    type: String,
    default: '',
    validator: (value) => ['small', 'normal', 'medium', 'large', 'massive', ''].includes(value),
  },
  icon: {
    type: String,
    default: '',
  },
  isGradient: Boolean,
  isOutlined: Boolean,
  isLoading: Boolean,
  isDisabled: Boolean,
  isFullWidth: Boolean,
  is3d: Boolean,
  isAnimated: Boolean,
  isOutlineGlow: Boolean,
  isPulse: Boolean,
});

const emit = defineEmits(['click']);

const buttonClasses = computed(() => [
  'button',
  'platform-button',
  props.size ? `is-${props.size}` : '',
  props.variant ? `is-${props.variant}` : '',
  {
    'is-gradient': props.isGradient,
    'is-outlined': props.isOutlined,
    'is-loading': props.isLoading,
    'is-disabled': props.isDisabled,
    'is-fullwidth': props.isFullWidth,
    'is-3d': props.is3d,
    'is-animated': props.isAnimated,
    'is-outline-glow': props.isOutlineGlow,
    'is-pulse': props.isPulse,
  },
]);

const handleClick = (event) => {
  if (!props.isDisabled && !props.isLoading) {
    emit('click', event);
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/abstracts' as a;

.button {
  font-weight: 500;
  transition: all 0.2s ease;
  height: auto;
  padding: 0.5rem 1rem;

  // Add consistent focus states
  &:focus {
    box-shadow: 0 0 0 0.125em rgba(a.$indiefy-blue, 0.25);
  }

  &.is-gradient {
    background: linear-gradient(90deg, a.$indiefy-blue, a.$indiefy-lavender);
    border: none;
    color: white;

    &:hover {
      background: linear-gradient(90deg, darken(a.$indiefy-blue, 5%), darken(a.$indiefy-lavender, 5%));
    }

    &:active {
      background: linear-gradient(90deg, darken(a.$indiefy-blue, 10%), darken(a.$indiefy-lavender, 10%));
    }
  }

  &.is-3d {
    transform: translateY(0);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }

  &.is-animated {
    position: relative;
    overflow: hidden;
    z-index: 1;

    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: -100%;
      background: linear-gradient(90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.2) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      transition: left 0.6s ease;
      z-index: -1;
    }

    &:hover::after {
      left: 100%;
    }
  }

  &.is-outline-glow {
    background: transparent;
    border: 2px solid var(--color-primary, a.$indiefy-blue);
    color: var(--color-primary, a.$indiefy-blue);
    transition: box-shadow 0.3s ease, background-color 0.3s ease;

    .body--dark & {
      border-color: white;
      color: white;
    }

    &:hover {
      background-color: rgba(a.$indiefy-blue, 0.1);
      box-shadow: 0 0 15px rgba(a.$indiefy-blue, 0.5);

      .body--dark & {
        background-color: rgba(255, 255, 255, 0.1);
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
      }
    }
  }

  &.is-massive {
    font-size: 1.2rem;
    padding: 1.5rem 2.5rem;
    height: auto;
    border-radius: 8px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  &.is-pulse {
    animation: pulse 2s infinite;
  }

  &.is-spotify {
    background: #1db954 !important;
    border-color: #1db954 !important;
    color: white !important;
    font-weight: 600;

    &:hover {
      background: #1ed760 !important;
      border-color: #1ed760 !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(29, 185, 84, 0.4);
    }

    &:active {
      background: #169c46 !important;
      border-color: #169c46 !important;
      transform: translateY(0);
    }

    &:focus {
      box-shadow: 0 0 0 0.125em rgba(29, 185, 84, 0.25);
    }
  }

  &.is-indiefy-pink {
    background: linear-gradient(135deg, a.$indiefy-pink, darken(a.$indiefy-pink, 10%)) !important;
    border: none !important;
    color: white !important;
    font-weight: 600;

    &:hover {
      background: linear-gradient(135deg, lighten(a.$indiefy-pink, 5%), darken(a.$indiefy-pink, 5%)) !important;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(a.$indiefy-pink, 0.4);
    }

    &:active {
      background: linear-gradient(135deg, darken(a.$indiefy-pink, 5%), darken(a.$indiefy-pink, 15%)) !important;
      transform: translateY(0);
    }

    &:focus {
      box-shadow: 0 0 0 0.125em rgba(a.$indiefy-pink, 0.25);
    }
  }

  &.is-indiefy-teal {
    background: linear-gradient(135deg, a.$indiefy-teal, darken(a.$indiefy-teal, 10%)) !important;
    border: none !important;
    color: white !important;
    font-weight: 600;

    &:hover {
      background: linear-gradient(135deg, lighten(a.$indiefy-teal, 5%), darken(a.$indiefy-teal, 5%)) !important;
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(a.$indiefy-teal, 0.4);
    }

    &:active {
      background: linear-gradient(135deg, darken(a.$indiefy-teal, 5%), darken(a.$indiefy-teal, 15%)) !important;
      transform: translateY(0);
    }

    &:focus {
      box-shadow: 0 0 0 0.125em rgba(a.$indiefy-teal, 0.25);
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(a.$indiefy-blue, 0.5);
  }

  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(a.$indiefy-blue, 0);
  }

  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(a.$indiefy-blue, 0);
  }
}
</style>